/**
 * Which forge a Loom project id belongs to.
 *
 * Loom stores every target repository as one `projectId: number`. A GitLab
 * project keeps its own positive id. A GitHub repository is stored as the
 * NEGATED GitHub repository id (`projectId = -repo.id`). The sign is the
 * forge: there is no separate `forge` column or parameter anywhere, and every
 * caller derives it from the id through `forgeOfProjectId`.
 *
 * Why the sign works:
 *   - every `project_id` column is `bigint` with no positive-only check, so a
 *     negative id needs no migration;
 *   - the two id spaces cannot collide in caches, primary keys, resume state or
 *     sessionStorage;
 *   - a GitLab call site that is not ported fails closed: the proxy profiles
 *     match only `\d+`, so `/api/v4/projects/-123/...` is refused before any
 *     token is used.
 *
 * `0` is neither: GitLab never issues it and it cannot be negated into a
 * GitHub id, so `projectIdSchema` refuses it.
 *
 * Browser-safe (no `node:` imports): `apps/web` bundles this module.
 */

import { z } from "zod";

export type Forge = "gitlab" | "github";

/** The forge a project id belongs to. Negative ids are GitHub repositories. */
export const forgeOfProjectId = (projectId: number): Forge =>
  projectId < 0 ? "github" : "gitlab";

/**
 * GitHub's own repository id for a Loom project id. Throws on a GitLab
 * (non-negative) id: asking for the repo id of a GitLab project is a
 * programming error, never a value to carry on with.
 */
export const githubRepoIdOf = (projectId: number): number => {
  if (!Number.isSafeInteger(projectId) || projectId >= 0) {
    throw new RangeError("githubRepoIdOf: not a GitHub project id");
  }
  return -projectId;
};

/** The Loom project id for a GitHub repository id (`-repoId`). */
export const projectIdOfGithubRepo = (repoId: number): number => {
  if (!Number.isSafeInteger(repoId) || repoId <= 0) {
    throw new RangeError("projectIdOfGithubRepo: not a GitHub repository id");
  }
  return -repoId;
};

/**
 * Is `s` a GitHub `owner/name` that is safe to splice into an API path or a
 * clone URL? GitHub's own character set, exactly one slash, and no segment
 * that is only dots (`.`/`..` pass the character class and would be normalised
 * out of a URL). The one check: the API client, the dispatcher's
 * `LOOM_TARGET_REPO` and the bridge env all mean the same shape.
 */
export const isGitHubFullName = (s: string): boolean =>
  /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/u.test(s) &&
  s.split("/").every((segment) => !/^\.+$/u.test(segment));

/**
 * A project id of either forge: a non-zero integer within ±1e12. Used where a
 * session-bound id is accepted (workflow inputs, session create) in place of a
 * positive-only check, so a GitHub id is not refused at the edge.
 */
export const projectIdSchema = z
  .number()
  .int()
  .refine((n) => n !== 0, { message: "projectId must not be 0" })
  .refine((n) => Math.abs(n) <= 1e12, { message: "projectId out of range" });
